import { useEffect } from "react";
import { format } from "date-fns";
import { X, Phone, Mail, MapPin, Package, Printer, User } from "lucide-react";
import { getSizeType } from "../../utils/itemUtils";

const OrderDetailsModal = ({ selectedOrder, onClose, updateStatus, statusColors, generateInvoice }) => {
    useEffect(() => {
        // Lock body scroll
        document.body.style.overflow = "hidden";
        return () => {
            // Restore body scroll
            document.body.style.overflow = "unset";
        };
    }, []);

    return (
        <div style={{ marginTop: "0px !important" }} className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">

                {/* Modal Header - Fixed */}
                <div className="flex-none bg-white border-b px-6 py-4 flex justify-between items-center z-10">
                    <div>
                        <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                            Order #{selectedOrder.orderId}
                            {selectedOrder.orderType === 'pickup' && (
                                <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-bold rounded uppercase">
                                    Pickup
                                </span>
                            )}
                        </h3>
                        <p className="text-sm text-gray-500">
                            Placed on {format(new Date(selectedOrder.createdAt), "MMMM d, yyyy 'at' h:mm a")}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-full transition"
                    >
                        <X size={20} className="text-gray-500" />
                    </button>
                </div>

                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                    {/* Status and Actions */}
                    <div className="flex flex-wrap items-center justify-between gap-4 bg-gray-50 p-4 rounded-xl border border-gray-100" >
                        <div className="flex items-center gap-3">
                            <span className="text-sm font-medium text-gray-600">Current Status:</span>
                            <span className={`px-3 py-1 rounded-full text-sm font-bold ${statusColors[selectedOrder.status]
                                }`}>
                                {selectedOrder.status.replace(/_/g, " ").toUpperCase()}
                            </span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-600">Update:</span>
                            <select
                                value={selectedOrder.status}
                                onChange={(e) => updateStatus(selectedOrder.orderId, e.target.value)}
                                className="border border-gray-300 rounded px-2 py-1 text-sm bg-white"
                            >
                                <option value="pending">Pending</option>
                                <option value="confirmed">Confirmed</option>
                                <option value="preparing">Preparing</option>
                                <option value="out_for_delivery">Out for Delivery</option>
                                <option value="delivered">Delivered</option>
                                <option value="cancelled">Cancelled</option>
                            </select>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Customer Information */}
                        <div className="space-y-4">
                            <h4 className="font-semibold text-gray-900 flex items-center gap-2 border-b pb-2">
                                <User size={18} className="text-green-600" />
                                Customer Details
                            </h4>
                            <div className="text-sm space-y-3">
                                <div>
                                    <p className="text-gray-500 text-xs">Name</p>
                                    <p className="font-medium text-gray-900">{selectedOrder.customerDetails?.name}</p>
                                </div>
                                <div>
                                    <p className="text-gray-500 text-xs">Phone</p>
                                    <div className="flex items-center gap-2">
                                        <p className="font-medium text-gray-900">{selectedOrder.customerDetails?.phone}</p>
                                        <a href={`tel:${selectedOrder.customerDetails?.phone}`} className="p-1 bg-green-50 text-green-600 rounded hover:bg-green-100">
                                            <Phone size={14} />
                                        </a>
                                    </div>
                                </div>
                                <div>
                                    <p className="text-gray-500 text-xs">Email</p>
                                    <div className="flex items-center gap-2">
                                        <p className="font-medium text-gray-900">{selectedOrder.customerDetails?.email}</p>
                                        <a href={`mailto:${selectedOrder.customerDetails?.email}`} className="p-1 bg-green-50 text-green-600 rounded hover:bg-green-100">
                                            <Mail size={14} />
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Delivery Information */}
                        <div className="space-y-4">
                            <h4 className="font-semibold text-gray-900 flex items-center gap-2 border-b pb-2">
                                <MapPin size={18} className="text-green-600" />
                                {selectedOrder.orderType === 'pickup' ? 'Pickup Order' : 'Delivery Location'}
                            </h4>
                            <div className="text-sm space-y-3">
                                <div>
                                    <p className="text-gray-500 text-xs">{selectedOrder.orderType === 'pickup' ? 'Customer Contact Address' : 'Address'}</p>
                                    <p className="font-medium text-gray-900 break-words">
                                        {selectedOrder.customerDetails?.address}
                                    </p>
                                </div>
                                {selectedOrder.location && (
                                    <div>
                                        <p className="text-gray-500 text-xs">Coordinates</p>
                                        <p className="text-gray-700">
                                            {selectedOrder.location.lat?.toFixed(6)}, {selectedOrder.location.lng?.toFixed(6)}
                                        </p>
                                        <a
                                            href={`https://www.google.com/maps/search/?api=1&query=${selectedOrder.location.lat},${selectedOrder.location.lng}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-block mt-1 text-xs text-blue-600 hover:underline"
                                        >
                                            Open in Google Maps
                                        </a>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Order Items */}
                    <div className="space-y-4">
                        <h4 className="font-semibold text-gray-900 flex items-center gap-2 border-b pb-2">
                            <Package size={18} className="text-green-600" />
                            Order Items
                        </h4>
                        <div className="bg-gray-50 rounded-xl p-4 space-y-4">
                            {selectedOrder.items.map((item, idx) => (
                                <div key={idx} className="flex justify-between items-start pb-4 border-b border-gray-200 last:border-0 last:pb-0">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2">
                                            <span className="font-bold text-green-600 bg-white border border-green-200 px-2 rounded-md">
                                                {item.quantity}x
                                            </span>
                                            <span className="font-medium text-gray-900">{item.name}</span>
                                        </div>
                                        <div className="mt-1 pl-10 text-sm text-gray-500 space-y-1">
                                            {item.selectedPortion && (
                                                <p>Size: <span className="text-gray-700">{getSizeType(item.selectedPortion, item)}</span></p>
                                            )}
                                            {item.addons && item.addons.length > 0 && (
                                                <div>
                                                    <p className="text-xs font-medium text-gray-400">Add-ons:</p>
                                                    <ul className="list-disc list-inside">
                                                        {item.addons.map((addon, aIdx) => (
                                                            <li key={aIdx}>
                                                                {addon.name} (+₹{addon.price})
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <p className="font-medium text-gray-900">₹{item.itemTotal || (item.price * item.quantity)}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Pricing Breakdown */}
                    <div className="bg-green-50 rounded-xl p-4 border border-green-100">
                        <h4 className="font-semibold text-gray-900 flex items-center gap-2 mb-4">
                            Bill Details
                        </h4>
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between text-gray-600">
                                <span>Item Total</span>
                                <span>₹{selectedOrder.pricing?.subtotal}</span>
                            </div>
                            <div className="flex justify-between text-gray-600">
                                <span>Delivery Fee</span>
                                <span>₹{selectedOrder.pricing?.deliveryFee}</span>
                            </div>
                            {selectedOrder.pricing?.discount > 0 && (
                                <div className="flex justify-between text-green-600">
                                    <span>Discount ({selectedOrder.promoCode ? `${selectedOrder.promoCode}` : "GUTFUL30"})</span>
                                    <span>-₹{selectedOrder.pricing?.discount}</span>
                                </div>
                            )}
                            <div className="border-t border-green-200 pt-3 mt-3 flex justify-between font-bold text-lg text-green-800">
                                <span>Grand Total</span>
                                <span>₹{selectedOrder.pricing?.total}</span>
                            </div>
                            <div className="text-right text-xs text-gray-500 mt-2">
                                Payment Method: <span className="font-medium uppercase">{selectedOrder.paymentMethod}</span>
                                <span className={`ml-2 px-2 py-0.5 rounded-full text-[10px] ${selectedOrder.paymentStatus === 'paid' ? 'bg-green-200 text-green-800' : 'bg-yellow-200 text-yellow-800'
                                    }`}>
                                    {selectedOrder.paymentStatus.toUpperCase()}
                                </span>
                            </div>
                        </div>
                    </div>

                </div>

                {/* Modal Footer - Fixed */}
                <div className="flex-none bg-gray-50 px-6 py-4 border-t flex justify-end">
                    <button
                        onClick={() => generateInvoice(selectedOrder)}
                        className="px-4 py-2 bg-green-600 border border-green-600 rounded-lg text-white hover:bg-green-700 font-medium transition flex items-center gap-2 mr-auto"
                    >
                        <Printer size={18} />
                        Download Invoice
                    </button>
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition"
                    >
                        Close Details
                    </button>
                </div>
            </div>
        </div>
    );
};

export default OrderDetailsModal;